/**
 * FlatLayService — Génération d'image flat-lay via Vertex AI Gemini 2.5 Flash Image
 *
 * Backend : POST /flat-lay
 * Body    : { section, items[], context }
 * Quota   : PARTAGÉ avec les try-ons (table tryon_usage) — 1 flat-lay = 1 crédit
 * Coût    : ~0.04 € par appel Vertex (chargé sur le quota tryon)
 *
 * Returns : { imageUrl, fromCache, section, cost_eur }
 */

import { getAuthToken } from './authHelpers';
import { saveResult } from './TryOnResultsService';

const API_URL = 'https://on0ym6si4l.execute-api.eu-west-3.amazonaws.com/prod';

/**
 * Génère un flat-lay pour une tenue.
 *
 * @param {object} outfit La tenue complète (items + section)
 * @param {object} opts { context?: { occasion, season, gender, ... } }
 * @returns {Promise<{ imageUrl, fromCache, cost_eur }>}
 * @throws Error si quota dépassé (e.code === 'quota_exceeded') ou autre erreur
 */
export async function generateFlatLay(outfit, opts = {}) {
  if (!outfit || !Array.isArray(outfit.items) || outfit.items.length === 0) {
    throw new Error('outfit invalide (items requis)');
  }

  const token = await getAuthToken().catch(() => null);
  if (!token) {
    const e = new Error('auth_required');
    e.code = 'auth_required';
    throw e;
  }

  // v1.12.876 — GA4 event flat_lay_started
  const _t0 = Date.now();
  try {
    const { trackEvent } = require('./AnalyticsService');
    trackEvent('flat_lay_started', {
      items_count: outfit.items.length,
      section: outfit.section || outfit.kind || 'my_looks',
      occasion: outfit.occasion || opts.context?.occasion || null,
      gender: outfit.gender || opts.context?.gender || null,
    });
  } catch {}

  // On envoie une projection minimale des items : seulement ce dont le backend
  // a besoin (id, category, image_url, color, name) pour le prompt Gemini.
  // v1.12.872 — Ajout photoUrl (DDB catalogue), photo (legacy), src — pour ne
  // pas perdre d'items dans le flat-lay (le backend filter items sans image).
  const items = outfit.items.map((it) => ({
    id: it.id || it.partner_product_id || it.product_id || null,
    category: it.category || it.cat || null,
    name: it.name || '',
    brand: it.brand || '',
    color: it.color || it.colorName || null,
    image: it.image || it.image_url || it.imageUrl || it.photoUrl || it.photo || it.src || null,
  }));
  // Log items sans image pour debug
  try {
    const skipped = items.filter(i => !i.image);
    if (skipped.length > 0) {
      console.warn('[FlatLay] items sans image (skipped backend):',
        skipped.map(s => `${s.category}/${s.name?.slice(0,30)}`).join(', '));
    }
  } catch {}

  const body = {
    section: outfit.section || outfit.kind || 'my_looks',
    items,
    // v1.12.996 — fond choisi par l'utilisateur (id, cf flatlayBackgrounds.js).
    // Le backend mappe l'id → fragment de prompt + l'intègre à la clé de cache.
    background: opts.background || 'cream',
    context: {
      occasion: outfit.occasion || opts.context?.occasion || null,
      season: outfit.season || opts.context?.season || null,
      gender: outfit.gender || opts.context?.gender || null,
      temperature: opts.context?.temperature ?? null,
    },
  };

  const res = await fetch(`${API_URL}/flat-lay`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (res.status === 429) {
    let detail = {};
    try { detail = await res.json(); } catch {}
    // v1.12.876 — Track quota
    try {
      const { trackEvent } = require('./AnalyticsService');
      trackEvent('flat_lay_quota_exceeded', { tier: detail?.tier, limit: detail?.limit });
    } catch {}
    const e = new Error(detail?.message || 'Quota atteint');
    e.code = 'quota_exceeded';
    e.detail = detail;
    throw e;
  }

  if (!res.ok) {
    const t = await res.text().catch(() => '');
    try {
      const { trackEvent } = require('./AnalyticsService');
      trackEvent('flat_lay_failed', { http_status: res.status });
    } catch {}
    throw new Error(`flat_lay_failed: HTTP ${res.status} ${t.slice(0, 120)}`);
  }

  const json = await res.json();
  const imageUrl = json?.imageUrl || json?.image_url || null;
  if (!imageUrl) throw new Error('Réponse invalide : imageUrl manquant');

  // v1.12.876 — GA4 event flat_lay_completed
  try {
    const { trackEvent } = require('./AnalyticsService');
    trackEvent('flat_lay_completed', {
      items_count: outfit.items.length,
      duration_ms: Date.now() - _t0,
      from_cache: !!json?.fromCache,
      cost_eur: Number(json?.cost_eur || 0),
      section: outfit.section || outfit.kind || 'my_looks',
    });
  } catch {}

  // Persister automatiquement le résultat pour cette tenue
  try {
    await saveResult({ outfit, kind: 'flatlay', imageUrl });
  } catch (e) {
    console.warn('[FlatLayService] saveResult failed:', e?.message);
  }

  // v1.12.766 — Centralise l'incrément du compteur try-on (flat-lay partage le
  // même quota côté backend). Si on ne le faisait que dans les composants UI,
  // un nouveau caller oubliant d'émettre laisserait le compteur ProfileScreen
  // stale. Ici : single source of truth, marche pour les 5 features (Tenue du
  // jour / Looks AI / Voyage / Mes Looks / Stylist).
  try {
    if (!json.fromCache && typeof json.usage === 'number') {
      const { setLocalUsage } = require('./TryOnService');
      await setLocalUsage(json.usage);
    }
  } catch {}
  try {
    const { DeviceEventEmitter } = require('react-native');
    DeviceEventEmitter.emit('tryon_usage_changed', {
      count: json.usage,
      limit: json.limit,
      tier: json.tier,
      isPaidTier: json.isPaidTier,
      nextResetDate: json.nextResetDate,
      reason: 'flatlay',
      fromCache: !!json.fromCache,
    });
  } catch {}

  return {
    imageUrl,
    fromCache: !!json.fromCache,
    cost_eur: json.cost_eur || 0,
    section: json.section,
    // v1.12.744 — Propager le quota mis à jour (compteur UI + popup abonnement)
    usage: json.usage,
    limit: json.limit,
    tier: json.tier,
    nextResetDate: json.nextResetDate,
    isPaidTier: json.isPaidTier,
  };
}
